import React, { useState, useEffect, useCallback } from "react";
import { semesterReviewService } from "../../services";

const DeletedSemesterReviewsPage = () => {
  const [deletedReviews, setDeletedReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [restoringId, setRestoringId] = useState(null);
  const [toast, setToast] = useState({
    show: false,
    message: "",
    type: "success",
  });

  const showToast = (message, type = "success") => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast((prev) => ({ ...prev, show: false })), 3500);
  };

  const loadDeleted = useCallback(async () => {
    setLoading(true);
    try {
      const data = await semesterReviewService.getDeletedReviews();
      setDeletedReviews(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error loading deleted reviews:", err);
      showToast("Could not load deleted entries.", "error");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDeleted();
  }, [loadDeleted]);

  const handleRestore = async (review) => {
    setRestoringId(review.id);
    try {
      await semesterReviewService.restoreReview(review.id);
      setDeletedReviews((prev) => prev.filter((r) => r.id !== review.id));
      showToast("Entry restored successfully.");
    } catch (err) {
      console.error("Error restoring review:", err);
      showToast("Could not restore entry. Please try again.", "error");
    } finally {
      setRestoringId(null);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleDateString("de-CH", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
  };

  return (
    <div className="min-h-screen bg-ice-white">
      {toast.show && (
        <div
          className={`fixed top-4 right-4 z-50 flex items-center gap-3 px-4 py-3 rounded-xl shadow-brand-lg text-sm font-semibold border ${
            toast.type === "error"
              ? "bg-red-50 text-red-700 border-red-200"
              : "bg-green-50 text-green-700 border-green-200"
          }`}
        >
          <i
            className={`fas ${toast.type === "error" ? "fa-exclamation-circle" : "fa-check-circle"}`}
          />
          {toast.message}
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="card-brand-elevated p-6 bg-gradient-to-r from-ice-white to-pale-dawn/30 mb-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-grey-dusk flex items-center mb-2">
                <i className="fas fa-trash-restore text-sunrise-red mr-4"></i>
                Deleted Entries
              </h1>
              <p className="text-slate-cloud">
                Restore deleted semester reviews and education reports safely.
              </p>
            </div>

            <button
              onClick={loadDeleted}
              disabled={loading}
              className="px-4 py-2 bg-white border border-sunrise-red/20 text-sunrise-red rounded-lg font-semibold hover:bg-sunrise-red hover:text-white transition-colors flex items-center justify-center gap-2"
            >
              <i className={`fas fa-sync-alt ${loading ? "fa-spin" : ""}`}></i>
              Refresh
            </button>
          </div>
        </div>

        <div className="card-brand overflow-hidden bg-white">
          <div className="bg-gradient-to-r from-sunrise-red to-sunrise-600 text-white px-5 py-4">
            <div className="flex items-center justify-between gap-3">
              <h2 className="text-lg font-bold flex items-center">
                <i className="fas fa-recycle mr-3"></i>
                Recycle Bin
              </h2>
              <span className="px-3 py-1 rounded-full bg-white/20 text-xs font-semibold">
                {deletedReviews.length} entries
              </span>
            </div>
          </div>

          <div className="p-5">
            {loading ? (
              <div className="flex flex-col items-center justify-center py-16 text-slate-cloud">
                <i className="fas fa-spinner fa-spin text-3xl text-sunrise-red mb-3"></i>
                <p>Loading deleted entries...</p>
              </div>
            ) : deletedReviews.length === 0 ? (
              <div className="text-center py-14">
                <div className="w-16 h-16 bg-green-50 border border-green-200 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <i className="fas fa-check-circle text-green-600 text-2xl"></i>
                </div>
                <h3 className="text-lg font-bold text-grey-dusk mb-1">
                  Recycle bin is empty
                </h3>
                <p className="text-sm text-slate-cloud">
                  There are currently no deleted semester reviews or education
                  reports.
                </p>
              </div>
            ) : (
              <div className="divide-y divide-pale-dawn border border-pale-dawn rounded-2xl overflow-hidden">
                {deletedReviews.map((review) => (
                  <div
                    key={review.id}
                    className="bg-white hover:bg-ice-white/70 transition-colors px-4 py-4"
                  >
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                      <div className="flex items-start gap-3 min-w-0">
                        <div className="w-10 h-10 rounded-xl bg-sunrise-red/10 text-sunrise-red flex items-center justify-center shrink-0">
                          <i className="fas fa-file-alt"></i>
                        </div>

                        <div className="min-w-0">
                          <h3 className="font-bold text-grey-dusk truncate">
                            {review.title || "Untitled entry"}
                          </h3>
                          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1 text-xs text-slate-cloud">
                            <span>
                              <i className="fas fa-user-graduate mr-1 text-sunrise-red"></i>
                              {review.learnerName || "—"}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 lg:min-w-[520px]">
                        <div className="rounded-xl bg-ice-white border border-pale-dawn px-3 py-2">
                          <div className="text-[11px] text-gray-500">
                            Deleted at
                          </div>
                          <div className="text-sm font-semibold text-grey-dusk">
                            {formatDate(review.deletedAt)}
                          </div>
                        </div>

                        <div className="rounded-xl bg-ice-white border border-pale-dawn px-3 py-2">
                          <div className="text-[11px] text-gray-500">
                            Deleted by
                          </div>
                          <div className="text-sm font-semibold text-grey-dusk truncate">
                            {review.deletedByName || "—"}
                          </div>
                        </div>

                        <button
                          onClick={() => handleRestore(review)}
                          disabled={restoringId === review.id}
                          className="rounded-xl bg-sunrise-red text-white px-4 py-2 text-sm font-semibold hover:bg-sunrise-600 disabled:opacity-50 transition-colors flex items-center justify-center"
                        >
                          {restoringId === review.id ? (
                            <i className="fas fa-spinner fa-spin mr-2"></i>
                          ) : (
                            <i className="fas fa-undo mr-2"></i>
                          )}
                          Restore
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {!loading && deletedReviews.length > 0 && (
            <div className="px-5 py-3 border-t border-pale-dawn bg-ice-white/40 text-xs text-gray-500">
              Showing {deletedReviews.length} deleted entries
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DeletedSemesterReviewsPage;
